/**
 * Test Case Constants
 * Contains all constants related to test cases
 */

import { STATUS_COLORS } from '@/constants/statusColors'

// Test case types (matching database schema)
export const TEST_CASE_TYPES = [
  { value: 'functional', label: 'Functional', color: STATUS_COLORS.FUNCTIONAL.badge },
  { value: 'non-functional', label: 'Non-Functional', color: STATUS_COLORS.NON_FUNCTIONAL.badge },
  { value: 'api', label: 'API', color: STATUS_COLORS.API.badge },
  { value: 'ui', label: 'UI', color: STATUS_COLORS.UI.badge },
  { value: 'performance', label: 'Performance', color: STATUS_COLORS.PERFORMANCE.badge },
  { value: 'security', label: 'Security', color: STATUS_COLORS.SECURITY.badge },
]

// Case types
export const CASE_TYPES = [
  { value: 'sanity', label: 'Sanity' },
  { value: 'smoke', label: 'Smoke' },
  { value: 'regression', label: 'Regression' }
]

// Automation status
export const AUTOMATION_STATUS = [
  { value: 'automated', label: 'Automated' },
  { value: 'manual', label: 'Manual' },
  { value: 'semi-automated', label: 'Semi-automated' }
]

// Automation status values (for easier access)
export const AUTOMATION_STATUS_VALUES = {
  AUTOMATED: 'automated',
  MANUAL: 'manual',
  SEMI_AUTOMATED: 'semi-automated'
}

// Test case status
export const TEST_CASE_STATUS = [
  { value: 'active', label: 'Active', color: STATUS_COLORS.ACTIVE.badge },
  { value: 'draft', label: 'Draft', color: STATUS_COLORS.DRAFT.badge },
  { value: 'deprecated', label: 'Deprecated', color: STATUS_COLORS.DEPRECATED.badge }
]

// Priority levels
export const PRIORITY_LEVELS = [
  { value: 'critical', label: 'Critical', color: STATUS_COLORS.CRITICAL.badge },
  { value: 'high', label: 'High', color: STATUS_COLORS.HIGH.badge },
  { value: 'medium', label: 'Medium', color: STATUS_COLORS.MEDIUM.badge },
  { value: 'low', label: 'Low', color: STATUS_COLORS.LOW.badge }
]

// Test case execution status (for test runs)
export const TEST_CASE_EXECUTION_STATUS = [
  { value: 'not_started', label: 'Not Started', color: STATUS_COLORS.NOT_STARTED.badge },
  { value: 'in_progress', label: 'In Progress', color: STATUS_COLORS.IN_PROGRESS.badge },
  { value: 'passed', label: 'Passed', color: STATUS_COLORS.PASSED.badge },
  { value: 'failed', label: 'Failed', color: STATUS_COLORS.FAILED.badge },
  { value: 'retest', label: 'Retest', color: STATUS_COLORS.RETEST.badge },
  { value: 'blocked', label: 'Blocked', color: STATUS_COLORS.BLOCKED.badge },
  { value: 'skipped', label: 'Skipped', color: STATUS_COLORS.SKIPPED.badge }
]

// Test case execution status values (auto-generated from TEST_CASE_EXECUTION_STATUS array)
export const TEST_CASE_EXECUTION_STATUS_VALUES = Object.fromEntries(
  TEST_CASE_EXECUTION_STATUS.map(status => [
    status.value.toUpperCase().replace(/([a-z])([A-Z])/g, '$1_$2'),
    status.value
  ])
)

// Legacy uppercase test case execution status constants (for backward compatibility)
export const TEST_CASE_EXECUTION_STATUS_LEGACY = {
  NOT_EXECUTED: 'NOT_EXECUTED',
  PASSED: 'PASSED',
  FAILED: 'FAILED'
}

// Helper functions to get options by value
export const getTestCaseTypeByValue = (value) => {
  return TEST_CASE_TYPES.find(type => type.value === value)
}

export const getCaseTypeByValue = (value) => {
  return CASE_TYPES.find(type => type.value === value)
}

export const getAutomationStatusByValue = (value) => {
  return AUTOMATION_STATUS.find(status => status.value === value)
}

export const getTestCaseStatusByValue = (value) => {
  return TEST_CASE_STATUS.find(status => status.value === value)
}

export const getPriorityByValue = (value) => {
  return PRIORITY_LEVELS.find(priority => priority.value === value)
}

export const getTestCaseExecutionStatusByValue = (value) => {
  return TEST_CASE_EXECUTION_STATUS.find(status => status.value === value)
}

// Helper functions to get color classes
export const getTestCaseTypeColor = (type) => {
  const typeConfig = getTestCaseTypeByValue(type)
  return typeConfig?.color || 'bg-gray-100 text-gray-800'
}

export const getTestCaseStatusColor = (status) => {
  const statusConfig = getTestCaseStatusByValue(status)
  return statusConfig?.color || 'bg-gray-100 text-gray-800'
}

export const getPriorityColor = (priority) => {
  const priorityConfig = getPriorityByValue(priority)
  return priorityConfig?.color || 'bg-gray-100 text-gray-800'
}

export const getTestCaseExecutionStatusColor = (status) => {
  const statusConfig = getTestCaseExecutionStatusByValue(status)
  return statusConfig?.color || 'bg-gray-100 text-gray-800'
}
